import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Apple, Play, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getApplications } from '../lib/api';
import { EmptyState, LoadingScreen, PageHero, SectionTag } from '../components/common/BrandElements';

const ApplicationsPage = () => {
  const { t } = useLanguage();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await getApplications();
        setApps(res.data.filter(a => a.is_active));
      } catch (error) {
        console.error('Error loading applications:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-raad-porcelain" data-testid="applications-page">
      <PageHero
        kicker={t('تطبيقاتنا', 'Our Applications')}
        title={t('منتجات رقمية بنقطة تركيز فيروزية', 'Digital products with a teal focus point')}
        description={t('تطبيقات وحلول تقنية تعكس جانب الابتكار في هوية رعد القابضة.', 'Apps and technical solutions expressing the innovation side of Raad Holding identity.')}
      />

      <section className="pb-24">
        <div className="container mx-auto px-6">
          {apps.length === 0 ? (
            <EmptyState title={t('لا توجد تطبيقات حالياً', 'No applications available')} />
          ) : (
            <div className="space-y-16">
              {apps.map((app, index) => {
                const screenshots = app.screenshots?.length ? app.screenshots.slice(0, 3) : ['/assets/raad-identity-hero.png'];
                return (
                  <motion.article
                    key={app.id}
                    initial={{ opacity: 0, y: 32 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, delay: index * 0.08 }}
                    className="identity-panel rounded-[2rem] border border-raad-teal/15 bg-white p-6 shadow-soft lg:p-10"
                  >
                    <div className={`relative z-10 grid gap-10 lg:grid-cols-2 lg:items-center ${index % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                      <div>
                        <SectionTag>{t('تطبيق رقمي', 'Digital App')}</SectionTag>
                        <div className="mt-5 flex items-center gap-4">
                          <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-raad-mist">
                            {app.icon_url ? (
                              <img src={app.icon_url} alt={t(app.name_ar, app.name_en)} className="h-14 w-14 object-contain" />
                            ) : (
                              <Smartphone className="h-9 w-9 text-raad-teal" />
                            )}
                          </div>
                          <h2 className="text-4xl font-black text-raad-charcoal text-balance">
                            {t(app.name_ar, app.name_en)}
                          </h2>
                        </div>
                        <p className="mt-6 text-lg leading-9 text-raad-graphite/70">
                          {t(app.description_ar, app.description_en)}
                        </p>

                        <div className="mt-8 flex flex-wrap gap-4">
                          {app.app_store_url && (
                            <Button asChild size="lg" className="rounded-full bg-raad-charcoal px-7 text-white hover:bg-raad-teal">
                              <a href={app.app_store_url} target="_blank" rel="noopener noreferrer">
                                <Apple className="w-5 h-5 me-2" />
                                App Store
                              </a>
                            </Button>
                          )}
                          {app.play_store_url && (
                            <Button asChild size="lg" className="rounded-full bg-raad-charcoal px-7 text-white hover:bg-raad-teal">
                              <a href={app.play_store_url} target="_blank" rel="noopener noreferrer">
                                <Play className="w-5 h-5 me-2" />
                                Google Play
                              </a>
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="flex min-h-[420px] items-center justify-center gap-4 overflow-hidden rounded-[1.75rem] bg-raad-charcoal p-8">
                        {screenshots.map((screenshot, i) => (
                          <div
                            key={`${screenshot}-${i}`}
                            className="relative h-80 w-40 overflow-hidden rounded-[2rem] border-[8px] border-raad-charcoal bg-white shadow-teal sm:h-96 sm:w-48"
                            style={{ transform: `rotate(${i === 1 ? 0 : i === 0 ? -7 : 7}deg) translateY(${i === 1 ? '-10px' : '16px'})` }}
                          >
                            <div className="absolute left-1/2 top-2 z-10 h-5 w-16 -translate-x-1/2 rounded-full bg-raad-charcoal" />
                            <img src={screenshot} alt={`${t(app.name_ar, app.name_en)} screenshot ${i + 1}`} className="h-full w-full object-cover" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ApplicationsPage;
